import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Camera, Loader2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENDERS = ['Male', 'Female', 'Non-binary'];

export default function CreatePersona() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    gender: '',
    description: '',
    avatar: '',
    is_default: false,
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, avatar: data.url }));
    } catch { alert('Failed to upload'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/personas`, form, { withCredentials: true });
      navigate(-1);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white font-['Unbounded'] text-sm">Create Persona</h1>
      </div>

      <div className="p-4 max-w-lg mx-auto space-y-6">
        {/* Avatar */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-[#1A1D24] border-2 border-white/10 flex items-center justify-center overflow-hidden">
              {form.avatar ? (
                <img src={form.avatar} alt="Persona" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center text-[#71717A]">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-8 h-8 bg-[#E11D48] rounded-full flex items-center justify-center border-2 border-[#050505]"
              data-testid="persona-avatar-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          </div>
        </div>

        {/* Name */}
        <div className="space-y-2">
          <Label className="text-white text-sm font-bold">My Name<span className="text-[#E11D48]">*</span></Label>
          <div className="relative">
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value.slice(0, 25) }))}
              placeholder="What do you want to be called?"
              className="bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-xl pr-14"
              data-testid="persona-name-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#71717A]">{form.name.length}/25</span>
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-[#71717A]">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            Letters, numbers, underscore, hyphen, and space only.
          </p>
        </div>

        {/* Gender */}
        <div className="space-y-2">
          <Label className="text-white text-sm font-bold">Gender</Label>
          <div className="grid grid-cols-3 gap-2">
            {GENDERS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, gender: prev.gender === g ? '' : g }))}
                className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                  form.gender === g
                    ? 'bg-[#E11D48] text-white border border-[#E11D48]'
                    : 'bg-[#1A1D24] text-[#A1A1AA] border border-white/10 hover:border-white/20'
                }`}
                data-testid={`gender-${g.toLowerCase()}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label className="text-white text-sm font-bold">My Description</Label>
          <div className="relative">
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value.slice(0, 700) }))}
              placeholder="What does the character know about you?"
              rows={5}
              className="bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-xl resize-none"
              data-testid="persona-description-input"
            />
            <span className="absolute right-3 bottom-3 text-[11px] text-[#71717A]">{form.description.length}/700</span>
          </div>
        </div>

        {/* Default Toggle */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-2">
          <Label className="text-white text-sm font-bold">Settings</Label>
          <div className="flex items-center justify-between">
            <span className="text-sm text-white">Set as Default Persona</span>
            <Switch
              checked={form.is_default}
              onCheckedChange={(val) => setForm(prev => ({ ...prev, is_default: val }))}
              data-testid="default-persona-toggle"
            />
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-[#71717A]">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            The default persona will automatically apply to all characters you chat with.
          </p>
        </div>

        {/* Save */}
        <Button
          onClick={handleSave}
          disabled={saving || !form.name.trim()}
          className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-xl py-3 disabled:opacity-40"
          data-testid="save-persona-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : 'Save'}
        </Button>
      </div>
    </div>
  );
}
